import React, { useEffect, useMemo, useState } from "react";
import { Eye, Pen, Plus, Trash2, Mail } from "lucide-react";
import { useOutletContext } from "react-router-dom";

import { DataTableNew } from "@/components/shared/DataTable";
import { Button } from "@/components/ui/Button";
import SearchInput from "@/components/shared/SearchInput";
import { Card } from "@/components/ui/Card";
import CopyId from "@/components/ui/CopyId";
import { Filter, FilterPill, FilterSelect } from "@/components/shared/Filter";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";

import toast from "react-hot-toast";
import Skeleton from "@/components/shared/Skeleton";

import { UserService } from "@/services/userService";

import AddEditUserModal from "./components/AddEditUserModal";
import DeleteUserModal from "./components/DeleteUserModal";
import ViewUserModal from "./components/ViewUserModal";
import StatusBadge from "@/components/shared/StatusBadge";
import { formatDate } from "@/utils/dateUtils";
import { useAuth } from "@/hooks/useAuth";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { userFormDefaultValues } from "./validation.schema";
import CustomTooltip from "@/components/shared/CustomTooltip";
import TenantScopeSelect from "@/components/shared/TenantScopeSelect";
import TableSkeleton from "@/components/shared/TableSkeleton";
import { useTenantScope } from "@/hooks/useTenantScope";

const UserPage = () => {
  const setHeader = useOutletContext();
  const { user } = useAuth();

  // A SuperAdmin works inside one tenant at a time, chosen here. Without
  // it the API can only answer from the platform database — shared tenants
  // merged together, dedicated tenants missing entirely.
  const { isSuperAdmin, tenants, tenantId, tenant, setTenantId, isLoading: tenantsLoading, isReady: scopeReady, scopeParams } = useTenantScope();

  // Which tenant the page is about. For a SuperAdmin that is the SELECTED
  // tenant, not the host they signed in on — the AD rules below (no manual
  // invites, the "Role Assignment Pending" status, no Invitation column)
  // belong to the tenant being managed, and a SuperAdmin's own session
  // carries no tenant's flag at all. A tenant admin only ever administers
  // their own, so theirs comes off the session.
  const adTenant = isSuperAdmin ? !!tenant?.adLoginEnabled : !!user.adLoginEnabled;

  const USER_STATUS_OPTIONS = useMemo(
    () => [{ label: "Active", value: "active" }, { label: "Inactive", value: "inactive" }, ...(adTenant ? [{ label: "Role Assignment Pending", value: "pending" }] : [])],
    [adTenant],
  );

  const [search, setSearch] = useState("");
  const [silentFetch, setSilentFetch] = useState(false);
  const [tableData, setTableData] = useState({ total: 0, isLoading: false });
  const [activeModal, setActiveModal] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [resendLoadingId, setResendLoadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setHeader({
      title: "Users List",
      subTitle: tableData.isLoading ? <Skeleton className="h-5 w-28" /> : `Total ${tableData.total} users`,
    });

    return () =>
      setHeader({
        title: "",
        subTitle: "",
      });
  }, [tableData, setHeader]);

  useEffect(() => {
    if (silentFetch) {
      setTimeout(() => {
        setSilentFetch(false);
      }, 200);
    }
  }, [silentFetch]);

  const handleSaveUser = async (formData) => {
    try {
      if (activeModal === "edit" && selectedUser?.id) {
        await UserService.updateUser(selectedUser.id, formData);
        toast.success("User updated");
      } else {
        await UserService.createUser(formData);
        toast.success("User added");
      }

      setActiveModal(null);
      setSelectedUser(null);
      setSilentFetch(true);
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessageFormat(e, `Failed to ${activeModal === "add" ? "save" : "update"} user`));
    }
  };

  const resendInvite = async (id) => {
    if (!id) return;

    setResendLoadingId(id);

    try {
      await UserService.resendInvite(id);
      toast.success("Invite resent");
    } catch (e) {
      console.error(e);
      const errorMessage = e?.response?.data?.message ?? e?.response?.data ?? "Failed to resend invite";
      toast.error(errorMessage);
    } finally {
      setResendLoadingId(null);
    }
  };

  const onDataFetch = (data) => {
    setTableData(data);
  };

  const defaultValuesForModal = useMemo(() => {
    if (activeModal === "edit" && selectedUser) {
      return {
        name: selectedUser.name ?? "",
        username: selectedUser.username ?? "",
        email: selectedUser.email ?? "",
        roleId: selectedUser.roleId ?? "",
        managerId: selectedUser.managerId ?? "",
        endDate: selectedUser.endDate ?? null,
      };
    }

    return userFormDefaultValues;
  }, [activeModal, selectedUser]);

  const getStatusMeta = (row) => {
    if (row?.isPending) {
      // Auto-provisioned by AD with a default role already in place (see
      // get_or_create_user on the server) — not blocked, just unreviewed.
      return {
        label: adTenant ? "Pending Review" : null,
        isActive: false,
        colorIndex: "2",
      };
    }

    if (row?.isActive) {
      return {
        label: "Active",
        isActive: true,
        colorIndex: 3,
      };
    }

    return {
      label: "Inactive",
      isActive: false,
      colorIndex: 1,
    };
  };

  const COLUMNS = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-left font-medium text-[13px]">{val ?? "—"}</span>,
    },
    {
      key: "username",
      label: "Username",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-left font-medium text-[13px]">{val ?? "—"}</span>,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-left font-medium text-[13px]">{val ?? "—"}</span>,
    },
    {
      key: "role",
      sortKey: "roleName",
      label: "Role",
      sortable: true,
      align: "left",
      render: (val, row) => <span className="text-left font-medium text-[13px]">{val?.name || row?.roleName || "—"}</span>,
    },
    {
      key: "manager",
      label: "Manager",
      sortable: true,
      align: "left",
      render: (val) => (
        <span className="flex items-center gap-2 text-left font-medium text-[13px]">
          {val?.name || "—"}
          <CopyId label="Manager" value={val?.id} />
        </span>
      ),
    },
    ...(!adTenant
      ? [
          {
            key: "isInvitationPending",
            label: "Invitation",
            sortable: true,
            align: "center",
            render: (_val, row) => {
              const pending = !!row?.isInvitationPending;
              const statusText = pending ? "Pending" : "Accept";
              const colorIndex = pending ? 2 : 3;

              return <StatusBadge isActive={!pending} text={statusText} colorIndex={colorIndex} />;
            },
          },
        ]
      : []),
    {
      key: "isPending",
      sortKey: "status",
      label: "Status",
      sortable: true,
      align: "center",
      render: (_val, row) => {
        const status = getStatusMeta(row);

        return <StatusBadge isActive={status.isActive} text={status.label} colorIndex={status.colorIndex} />;
      },
    },
    {
      key: "endDate",
      label: "Validate Date",
      sortable: true,
      align: "center",
      wrap: true,
      render: (val) => <span className="text-text-primary text-[12px]">{val ? formatDate(val) : "—"}</span>,
    },
    {
      key: "id",
      label: "Actions",
      sortable: false,
      align: "center",
      render: (_val, row) => {
        return (
          <div className="flex gap-2 justify-center">
            <PermissionGuard permissions={[PERMISSIONS.USER_VIEW, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
              <CustomTooltip text="View User">
                <Button
                  onClick={() => {
                    setSelectedUser(row);
                    setActiveModal("view");
                  }}
                  className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                >
                  <Eye size={16} />
                </Button>
              </CustomTooltip>
            </PermissionGuard>

            <PermissionGuard permissions={[PERMISSIONS.USER_UPDATE, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
              <CustomTooltip text="Edit User">
                <Button
                  onClick={() => {
                    setSelectedUser(row);
                    setActiveModal("edit");
                  }}
                  className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                  disabled={user.id === row?.id}
                >
                  <Pen size={16} />
                </Button>
              </CustomTooltip>
            </PermissionGuard>

            {!row?.isDeleted && (
              <PermissionGuard permissions={[PERMISSIONS.USER_DELETE, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
                <CustomTooltip text="Delete User">
                  <Button
                    onClick={() => {
                      setDeletingUserId(row?.id);
                      setActiveModal("delete");
                    }}
                    className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
                    disabled={user.id === row?.id}
                  >
                    <Trash2 size={16} />
                  </Button>
                </CustomTooltip>
              </PermissionGuard>
            )}

            {/* Only where a resend can actually land. `isInvitationPending` is
                the server's own answer to "was this person invited and never
                accepted" — local provider, no password set — so an
                AD-provisioned row (which authenticates against the directory
                and never sets a password) and an account that accepted and was
                later deactivated both drop out. And never on an AD tenant:
                all three invite routes carry `require_non_ad_tenant()`, and
                that is where every account starts inactive and pending, so
                the button used to sit on the whole list and every click
                answered 403. The Add User button beside it is wrapped in the
                same flag. */}
            {!adTenant && row?.isInvitationPending && (
              <PermissionGuard permissions={[PERMISSIONS.USER_CREATE, PERMISSIONS.SUPER_ADMIN_MANAGE]} isComponent>
                <Button
                  onClick={() => resendInvite(row?.id)}
                  disabled={resendLoadingId === row?.id}
                  className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60"
                  title="Resend invite"
                >
                  <Mail size={16} />
                </Button>
              </PermissionGuard>
            )}
          </div>
        );
      },
    },
  ];

  const filters = useMemo(
    () => [
      {
        key: "status",
        label: "All Statuses",
        value: statusFilter,
        onChange: setStatusFilter,
        options: USER_STATUS_OPTIONS,
      },
    ],
    [statusFilter, USER_STATUS_OPTIONS],
  );

  const activeFilters = useMemo(() => {
    const selectedStatus = USER_STATUS_OPTIONS.find((s) => s.value === statusFilter);

    return [
      selectedStatus && {
        key: "status",
        label: `Status: ${selectedStatus.label}`,
        clear: () => setStatusFilter(""),
      },
    ].filter(Boolean);
  }, [statusFilter, USER_STATUS_OPTIONS]);

  const handleClearAll = () => {
    setStatusFilter("");
  };

  const extraParams = useMemo(() => {
    if (!statusFilter) {
      return {};
    }

    if (statusFilter === "active") {
      return {
        isPending: false,
        isActive: true,
      };
    }

    if (statusFilter === "inactive") {
      return {
        isPending: false,
        isActive: false,
      };
    }

    if (statusFilter === "pending") {
      return {
        isPending: true,
      };
    }

    return {};
  }, [statusFilter]);

  return (
    <PermissionGuard permissions={[PERMISSIONS.USER_VIEW, PERMISSIONS.SUPER_ADMIN_MANAGE]}>
      <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
        <div className="flex items-center gap-2.5">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Search users..." />
          </div>

          <TenantScopeSelect isSuperAdmin={isSuperAdmin} tenants={tenants} tenantId={tenantId} onChange={setTenantId} isLoading={tenantsLoading} />

          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((p) => !p)}
            onClearAll={handleClearAll}
            renderFilter={(f) => <FilterSelect key={f.key} {...f} />}
            renderActiveFilter={(f) => <FilterPill key={f.key} label={f.label} onRemove={f.clear} />}
          />

          {!adTenant && scopeReady && (
            <PermissionGuard permission={PERMISSIONS.USER_CREATE} isComponent>
              <Button
                onClick={() => {
                  setSelectedUser(null);
                  setActiveModal("add");
                }}
                className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2 items-center gap-1 text-[11px] whitespace-nowrap shrink-0"
              >
                <Plus size={11} />
                Add User
              </Button>
            </PermissionGuard>
          )}
        </div>

        {/* Mobile floating action button */}
        {!adTenant && scopeReady && (
          <div className="fixed bottom-0 left-0 right-0 md:hidden px-4 py-3">
            <PermissionGuard permission={PERMISSIONS.USER_CREATE} isComponent>
              <Button
                onClick={() => {
                  setSelectedUser(null);
                  setActiveModal("add");
                }}
                className="w-full bg-button-secondary text-text-base rounded-full py-2.5 flex items-center justify-center gap-1.5 text-[13px]"
              >
                <Plus size={14} />
                Add User
              </Button>
            </PermissionGuard>
          </div>
        )}

        <Card className="p-0">
          {/* Held until a SuperAdmin has a tenant, so the first
                        request is never an unscoped cross-tenant read. The
                        table key carries the tenant: a new scope is a new
                        list, not page 3 of the old one. */}
          {scopeReady ? (
            <DataTableNew
              key={`users-list-${tenantId || "self"}`}
              className="[&_tbody_tr:hover>td]:bg-table-heading [&_tbody_tr:focus-within>td]:bg-table-heading"
              columns={COLUMNS}
              api={UserService.fetchUsersList}
              search={search}
              dataPath="users"
              onDataFetch={onDataFetch}
              silentFetch={silentFetch}
              extraParams={{
                isDeleted: false,
                ...extraParams,
                ...scopeParams,
              }}
            />
          ) : (
            <TableSkeleton />
          )}
        </Card>

        <AddEditUserModal
          isOpen={activeModal === "add" || activeModal === "edit"}
          mode={activeModal === "edit" ? "edit" : "add"}
          onModelClose={() => {
            setActiveModal(null);
            setSelectedUser(null);
          }}
          onHandleSubmit={handleSaveUser}
          defaultValues={defaultValuesForModal}
          editUserId={selectedUser?.id || ""}
        />

        <DeleteUserModal
          open={activeModal === "delete"}
          onClose={() => {
            setActiveModal(null);
            setDeletingUserId(null);
          }}
          userId={deletingUserId}
          loading={false}
          setSilentFetch={setSilentFetch}
        />

        <ViewUserModal
          open={activeModal === "view"}
          onClose={() => {
            setActiveModal(null);
            setSelectedUser(null);
          }}
          user={selectedUser}
        />
      </div>
    </PermissionGuard>
  );
};

export default UserPage;
